import { type AppRoute, AppRoutes, appUrl } from '../app.route.constants';

export interface NavItem {
  readonly labelKey: string;
  readonly icon: string;
  readonly link: `/${string}`;
  readonly exact?: boolean;
  readonly descriptionKey?: string;
  /** Items waiting in a validation queue. Shown as a count badge. */
  readonly pendingCount?: number;
  /** Shows the live number of notices awaiting validation as the badge. */
  readonly pendingNotices?: boolean;
  /** Hidden from users who cannot validate. */
  readonly validatorOnly?: boolean;
  /** Draws the icon in the success color, used for validation actions. */
  readonly tone?: 'success';
}

export interface NavSection {
  readonly id: string;
  readonly labelKey: string;
  readonly items: readonly NavItem[];
}

export const HOME_ITEM: NavItem = {
  labelKey: 'nav.home.label',
  icon: 'heroHome',
  link: appUrl(AppRoutes.HOME),
  exact: true,
  descriptionKey: 'nav.home.description',
};

export const NAV_SECTIONS: readonly NavSection[] = [
  {
    id: 'notices',
    labelKey: 'nav.notices.sectionLabel',
    items: [
      {
        labelKey: 'nav.notices.create.label',
        icon: 'heroPencilSquare',
        link: appUrl(AppRoutes.CREATE_NOTICE),
        descriptionKey: 'nav.notices.create.description',
      },
      {
        labelKey: 'nav.notices.mine.label',
        icon: 'heroClipboardDocumentList',
        link: appUrl(AppRoutes.MY_NOTICES),
        descriptionKey: 'nav.notices.mine.description',
      },
      {
        labelKey: 'nav.notices.validate.label',
        icon: 'heroCheckCircle',
        link: appUrl(AppRoutes.VALIDATE_NOTICE),
        descriptionKey: 'nav.notices.validate.description',
        pendingNotices: true,
        validatorOnly: true,
        tone: 'success',
      },
    ],
  },
  {
    id: 'preparation',
    labelKey: 'nav.preparation.sectionLabel',
    items: [
      {
        labelKey: 'nav.preparation.history.label',
        icon: 'heroClock',
        link: appUrl(AppRoutes.HISTORY),
        descriptionKey: 'nav.preparation.history.description',
      },
      {
        labelKey: 'nav.preparation.edmsDocuments.label',
        icon: 'heroFolder',
        link: appUrl(AppRoutes.EDMS_DOCUMENTS),
        descriptionKey: 'nav.preparation.edmsDocuments.description',
      },
      {
        labelKey: 'nav.preparation.spareParts.label',
        icon: 'heroCog6Tooth',
        link: appUrl(AppRoutes.SPARE_PARTS),
        descriptionKey: 'nav.preparation.spareParts.description',
      },
    ],
  },
  {
    id: 'work-orders',
    labelKey: 'nav.workOrders.sectionLabel',
    items: [
      {
        labelKey: 'nav.workOrders.portfolio.label',
        icon: 'heroBriefcase',
        link: appUrl(AppRoutes.WORK_ORDER_PORTFOLIO),
        descriptionKey: 'nav.workOrders.portfolio.description',
      },
      {
        labelKey: 'nav.workOrders.planning.label',
        icon: 'heroCalendarDays',
        link: appUrl(AppRoutes.OPERATIONS_PLANNING),
        descriptionKey: 'nav.workOrders.planning.description',
      },
    ],
  },
  {
    id: 'intervention-reports',
    labelKey: 'nav.interventionReports.sectionLabel',
    items: [
      {
        labelKey: 'nav.interventionReports.create.label',
        icon: 'heroClipboardDocumentList',
        link: appUrl(AppRoutes.INTERVENTION_REPORT),
        descriptionKey: 'nav.interventionReports.create.description',
      },
      {
        labelKey: 'nav.interventionReports.validate.label',
        icon: 'heroDocumentCheck',
        link: appUrl(AppRoutes.VALIDATE_INTERVENTION_REPORT),
        descriptionKey: 'nav.interventionReports.validate.description',
        pendingCount: 4,
      },
    ],
  },
];

const FEATURE_ROUTES: readonly AppRoute[] = NAV_SECTIONS.flatMap((section) =>
  section.items.map((item) => item.link.slice(1) as AppRoute),
);

export const FEATURE_PATHS: readonly AppRoute[] = FEATURE_ROUTES;

function pathOf(url: string): string {
  return url.split(/[?#]/)[0] ?? url;
}

export function findNavItem(url: string): NavItem | undefined {
  const path = pathOf(url);
  const items = [HOME_ITEM, ...NAV_SECTIONS.flatMap((section) => section.items)];
  return items.find((item) => path === item.link || path.startsWith(`${item.link}/`));
}

export interface PageMetaKeys {
  readonly titleKey: string;
  readonly subtitleKey: string;
}

const SETTINGS_LINK = appUrl(AppRoutes.SETTINGS);
const MODIFY_LINK = appUrl(AppRoutes.MODIFY_NOTICE);

export function pageMetaFromUrl(url: string): PageMetaKeys {
  const path = pathOf(url);
  if (path === HOME_ITEM.link || path.startsWith(`${HOME_ITEM.link}/`)) {
    return { titleKey: HOME_ITEM.labelKey, subtitleKey: 'nav.home.description' };
  }

  if (path === SETTINGS_LINK || path.startsWith(`${SETTINGS_LINK}/`)) {
    return { titleKey: 'settings.title', subtitleKey: 'settings.description' };
  }

  if (path.startsWith(`${MODIFY_LINK}/`)) {
    return { titleKey: 'nav.notices.modify.label', subtitleKey: 'nav.notices.sectionLabel' };
  }

  for (const section of NAV_SECTIONS) {
    const item = section.items.find(
      (entry) => path === entry.link || path.startsWith(`${entry.link}/`),
    );
    if (item) return { titleKey: item.labelKey, subtitleKey: section.labelKey };
  }

  return { titleKey: 'notFound.title', subtitleKey: 'notFound.code' };
}
